import {
  type MutableRefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import {
  type Auth0ProviderConfig,
  getAuth0AccountLinkErrorMessage,
  useProviderConnect,
} from '@spotify/confidence-auth0-react';
import { useAlert } from '@spotify/confidence-core-react';

import { type ChatAttachment, type PendingAttachment } from '../../types';

type LinkPasteResult = { url: string };

interface UseLinkPasteOptions<T extends LinkPasteResult> {
  providerConfig: Auth0ProviderConfig;
  sourceType:
    | PendingAttachment['sourceType']
    | ((result: T) => PendingAttachment['sourceType']);
  extractSourceId: (result: T) => string;
  providerLabel: string;
  duplicateLabel: string;
  attachments?: ChatAttachment[];
  onAttached?: (attachment: ChatAttachment) => void;
}

interface UseLinkPasteReturn<T extends LinkPasteResult> {
  onPastedRef: MutableRefObject<((result: T) => void) | null>;
  showBanner: boolean;
  isConnecting: boolean;
  connectionError: string | null;
  handleConnect: () => Promise<void>;
}

export const useLinkPaste = <T extends LinkPasteResult>({
  providerConfig,
  sourceType,
  extractSourceId,
  providerLabel,
  duplicateLabel,
  attachments,
  onAttached,
}: UseLinkPasteOptions<T>): UseLinkPasteReturn<T> => {
  const alertApi = useAlert();

  const [pendingPaste, setPendingPaste] = useState<T | null>(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [connectionError, setConnectionError] = useState<string | null>(null);

  const extractSourceIdRef = useRef(extractSourceId);
  extractSourceIdRef.current = extractSourceId;

  const { isConnected, isCheckingConnection, connect } = useProviderConnect(
    providerConfig,
    {
      isDialogOpen: !!pendingPaste && !!onAttached,
    },
  );

  const attachPaste = useCallback(
    (result: T) => {
      if (!onAttached) return;

      const sourceId = extractSourceIdRef.current(result);
      const alreadyAttached = attachments?.some(
        a => a.kind === 'pending' && a.sourceId === sourceId,
      );
      if (alreadyAttached) {
        alertApi.post({
          severity: 'info',
          message: `This ${duplicateLabel} is already attached.`,
        });
        return;
      }

      onAttached({
        kind: 'pending',
        sourceType:
          typeof sourceType === 'function' ? sourceType(result) : sourceType,
        sourceId,
        title: result.url,
        uri: result.url,
      });
      setPendingPaste(null);
    },
    [alertApi, attachments, duplicateLabel, onAttached, sourceType],
  );

  const onPastedRef = useRef<((result: T) => void) | null>(null);

  onPastedRef.current = onAttached
    ? (result: T) => {
        setConnectionError(null);
        if (!isConnected) {
          setPendingPaste(result);
          return;
        }
        attachPaste(result);
      }
    : null;

  const handleConnect = useCallback(async () => {
    setIsConnecting(true);
    setConnectionError(null);
    try {
      const { linked, failureReason } = await connect();
      if (!linked) {
        setConnectionError(
          failureReason ?? `Failed to connect your ${providerLabel} account`,
        );
        return;
      }
    } catch (err) {
      setConnectionError(
        getAuth0AccountLinkErrorMessage(
          err,
          `Failed to connect your ${providerLabel} account`,
        ),
      );
    } finally {
      setIsConnecting(false);
    }
  }, [connect, providerLabel]);

  useEffect(() => {
    if (pendingPaste && isConnected) {
      attachPaste(pendingPaste);
    }
  }, [attachPaste, pendingPaste, isConnected]);

  const showBanner =
    !!pendingPaste && !!onAttached && !isConnected && !isCheckingConnection;

  return {
    onPastedRef,
    showBanner,
    isConnecting,
    connectionError,
    handleConnect,
  };
};
